<template>
  <Modal
    :isOpen="isOpen"
    @update:isOpen="$emit('update:isOpen', $event)"
    modalId="create-customer-modal"
    :title="`New ${showingCustomerType === 'billing' ? 'Customer' : 'Receiver'}`"
    subtitle="Create a new record and optionally save addresses."
    maxWidth="7xl"
    @close="onClose"
  >
    <template #content="{ close }">
      <div v-if="loadFormError"
        class="tw-mb-4 tw-rounded-xl tw-border tw-border-rose-200/70 tw-bg-rose-50 tw-px-4 tw-py-3 tw-text-sm tw-text-rose-900">
        {{ loadFormError }}
      </div>

      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
        <InputField id="customer_name" label="* Customer Name (required)" v-model="name" class="tw-w-full">
        </InputField>
        <InputField id="customer_email" label="Customer Email (recommended)" v-model="email" class="tw-w-full">
        </InputField>
      </div>

      <div class="tw-mt-5 tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
        <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-p-4">
          <div class="tw-text-sm tw-font-semibold tw-text-slate-900">Drop-off address</div>
          <div class="tw-mt-3 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
            <GoogleAddressInput id="receiver_street" label="Street Address" v-model="receiverAddress.streetAddress"
              class="sm:tw-col-span-2" @address-selected="handleReceiverAddress" />
            <InputField id="receiver_suite" label="Suite #" v-model="receiverAddress.suite"></InputField>
            <InputField id="receiver_city" label="City" v-model="receiverAddress.city"></InputField>
            <InputField id="receiver_state" label="State" v-model="receiverAddress.state"></InputField>
            <InputField id="receiver_zip" label="Zip" v-model="receiverAddress.zipCode"></InputField>
          </div>
        </div>

        <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-p-4">
          <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
            <div class="tw-text-sm tw-font-semibold tw-text-slate-900">Billing address</div>
            <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
              <input type="checkbox" v-model="useDropOffAddress"
                class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300" />
              Same as drop-off
            </label>
          </div>

          <div v-if="!useDropOffAddress" class="tw-mt-3 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
            <GoogleAddressInput id="billing_street" label="Street Address"
              v-model="billingAddress.streetAddress" class="sm:tw-col-span-2"
              @address-selected="handleBillingAddress" />
            <InputField id="billing_suite" label="Suite #" v-model="billingAddress.suite"></InputField>
            <InputField id="billing_city" label="City" v-model="billingAddress.city"></InputField>
            <InputField id="billing_state" label="State" v-model="billingAddress.state"></InputField>
            <InputField id="billing_zip" label="Zip" v-model="billingAddress.zipCode"></InputField>
          </div>
          <div v-else class="tw-mt-3 tw-text-sm tw-text-slate-600">
            Billing address will be copied from drop-off.
          </div>
        </div>
      </div>

      <div v-if="loading" class="tw-mt-4 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-600">
        <font-awesome-icon icon="circle-notch" spin class="tw-h-4 tw-w-4 tw-text-slate-500" />
        Saving…
      </div>

      <div class="tw-mt-5 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200/70 tw-pt-4">
        <button type="button"
          class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
          @click="close">
          Cancel
        </button>
        <button type="button"
          class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
          v-on:click="createNewCustomer" :disabled="!isFormComplete || loading">
          Add New
        </button>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { toSentenceCase } from '../../utils/formatter';
import { showGraphQLError } from '../../utils/data_extraction';

import CREATE_CUSTOMER from '../../gql/customers/CreateCustomer.gql';
import Modal from '@/components/core/Modal/Modal.vue';
import InputField from '../core/Input/InputField.vue';
import GoogleAddressInput from '../core/GoogleAddressInput.vue';

export default defineComponent({
  name: 'CreateCustomerModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    showingCustomerType: {
      type: String,
      default: 'billing',
    },
  },
  emits: ['update:isOpen', 'close', 'createdCustomer'],
  components: {
    Modal,
    InputField,
    GoogleAddressInput,
  },
  data() {
    return {
      useDropOffAddress: false,
      loading: false,
      loadFormError: null,
      textDanger: false,
      receiverAddress: {
        suite: null,
        streetAddress: null,
        city: null,
        state: null,
        zipCode: null,
      },
      billingAddress: {
        suite: null,
        streetAddress: null,
        city: null,
        state: null,
        zipCode: null,
      },
      name: null,
      email: null,
      phone: null,
    };
  },
  watch: {
    name(val) {
      if (val) {
        (this as any).name = toSentenceCase(val);
      }
    },
  },
  computed: {
    isFormComplete(): boolean {
      return !!(this as any).name;
    },
  },
  methods: {
    handleReceiverAddress(data: any) {
      const vm = this as any;
      vm.receiverAddress.streetAddress = data.streetAddress;
      vm.receiverAddress.city = data.city;
      vm.receiverAddress.state = data.state;
      vm.receiverAddress.zipCode = data.zipCode;
    },
    handleBillingAddress(data: any) {
      const vm = this as any;
      vm.billingAddress.streetAddress = data.streetAddress;
      vm.billingAddress.city = data.city;
      vm.billingAddress.state = data.state;
      vm.billingAddress.zipCode = data.zipCode;
    },
    onClose() {
      (this as any).$emit('close');
    },
    createNewCustomer() {
      const vm = (this as any);
      vm.loading = true;

      const variables = {
        module: 'CONTRAK',
        name: vm.name,
        email: vm.email,
        receiverAddress: vm.receiverAddress,
        billingAddress: vm.useDropOffAddress ? vm.receiverAddress : vm.billingAddress,
      };

      vm.$apollo
        .mutate({
          mutation: CREATE_CUSTOMER,
          variables,
        })
        .then(() => {
          vm.loading = false;
          vm.loadFormError = null;

          vm.name = null;
          vm.email = null;
          vm.phone = null;
          vm.receiverAddress = {
            suite: null,
            streetAddress: null,
            city: null,
            state: null,
            zipCode: null,
          };
          vm.billingAddress = {
            suite: null,
            streetAddress: null,
            city: null,
            state: null,
            zipCode: null,
          };

          vm.$emit('createdCustomer');
          vm.$emit('update:isOpen', false);
        })
        .catch((error: any) => {
          vm.loadFormError = showGraphQLError(error);
          vm.loading = false;
        });
    },
  },
});
</script>
